// CampaignPage pagination uses real Next.js navigation through <Link>, same
// rationale as the search results pagination — viewport prefetch warms the
// RSC payload before the click so navigation feels instant, and the server
// stays canonical (no manual title/canonical sync needed). See
// src/app/search/[[...slug]]/containers/Pagination.tsx for the full reasoning.

'use client';

import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import {
  PaginationLink,
  PaginationNavLink,
  Pagination as StyledPagination,
} from '@//components/Pagination';
import type { ElasticSearchResponseDocumentListPagination } from '@//ElasticSearch';
import { limitNumberOfPaginationPages } from '@//utils/pagination';

const buildHref = (rootUrl: string, currentParams: URLSearchParams, query?: string) => {
  const next = new URLSearchParams();
  // Preserve sort from current URL
  const sort = currentParams.get('sort');
  if (sort) next.set('sort', sort);

  // Set offset from the API-provided pagination query
  if (query) {
    const source = new URLSearchParams(query);
    const offset = source.get('offset');
    if (offset && offset !== '0') {
      next.set('offset', offset);
    }
  }

  const queryString = next.toString();
  return `${rootUrl}${queryString ? `?${queryString}` : ''}`;
};

const scrollToHeading = () => {
  // Instant scroll: the results are already in the DOM (SSR + initialData), so
  // there's no loading state to mask. A smooth animation would just delay the
  // landing position without adding information.
  const heading = document.getElementById('campaign-vehicles-heading');
  const grid = document.getElementById('campaign-vehicles');
  (heading ?? grid)?.scrollIntoView({ behavior: 'instant', block: 'start' });
};

interface PaginationProps {
  rootUrl: string;
  pagination?: ElasticSearchResponseDocumentListPagination;
}

const Pagination = ({ rootUrl, pagination }: PaginationProps) => {
  const t = useTranslations('other.pagination');
  const currentParams = useSearchParams();

  const onClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    (e.target as HTMLElement).blur();
    scrollToHeading();
    document.getElementById('campaign-vehicles')?.focus({ preventScroll: true });
  }, []);

  if (!pagination || pagination.pages.length <= 1) {
    return null;
  }

  const prev = pagination.previousPage?.query
    ? buildHref(rootUrl, currentParams, pagination.previousPage.query)
    : undefined;
  const next = pagination.nextPage?.query
    ? buildHref(rootUrl, currentParams, pagination.nextPage.query)
    : undefined;

  const pages = limitNumberOfPaginationPages(pagination.pages);

  return (
    <StyledPagination testId="campaign-page-pagination" ariaLabel={t('ariaLabel')} center>
      <PaginationNavLink
        variant="prev"
        href={prev}
        testId="campaign-page-pagination-prev"
        onClick={prev ? onClick : undefined}
        scroll={false}
      />
      {pages.map((page) => {
        const href = buildHref(rootUrl, currentParams, page.query);
        return (
          <PaginationLink
            key={page.displayName}
            href={href}
            onClick={onClick}
            title={t('goTo.title', { page: page.displayName })}
            testId={`campaign-page-pagination-page-${page.displayName}`}
            isActive={page.selected}
            scroll={false}
          >
            {page.displayName}
          </PaginationLink>
        );
      })}
      <PaginationNavLink
        variant="next"
        href={next}
        testId="campaign-page-pagination-next"
        onClick={next ? onClick : undefined}
        scroll={false}
      />
    </StyledPagination>
  );
};

export default Pagination;
